/**
 * DuplicateTabModal — informational modal shown when this tab detects another
 * tab on the same device is already editing the same resource. Recommends the
 * user switch to the other tab; on dismiss, the parent renders the form inert
 * (read-only) so the user can browse data here without accidentally starting
 * a competing edit session.
 *
 * No takeover path — that's intentional. If the user actually wants to edit
 * here, they close the other tab (or wait 10 minutes for the lock TTL) and
 * the auto-takeover behavior on next mount handles the lock dance.
 */

import React from 'react';
import { buttonClass } from './ui/Button';
import { Dialog } from './ui/Dialog';
import { IconWarning } from './ui/icons';

interface Props {
  resourceLabel?: string;
  onDismiss: () => void;
}

const DuplicateTabModal: React.FC<Props> = ({ resourceLabel = 'this event', onDismiss }) => {
  return (
    <Dialog
      open
      onClose={() => {}}
      dismissible={false}
      hideClose
      alert
      size="lg"
      title={
        <span className="flex items-center gap-3 text-amber-200">
          <IconWarning className="h-6 w-6 shrink-0 text-amber-400" />
          You already have this tab open
        </span>
      }
      footer={
        <button type="button" onClick={onDismiss} className={buttonClass('filled', 'md')}>
          Got it
        </button>
      }
    >
      <div className="space-y-3">
        <p className="m-0 text-sm text-on-surface">
          {resourceLabel.charAt(0).toUpperCase() + resourceLabel.slice(1)} is already being edited in another browser tab on this device.
        </p>
        <p className="m-0 text-sm text-on-surface">
          Switch back to that tab to keep editing, your unsaved typing is safe there. (We flashed its title so it's easy to find.)
        </p>
        <p className="m-0 text-xs text-on-surface-variant">
          To avoid lost edits, this tab won't acquire the editor. You can dismiss this and view the form in read-only mode.
        </p>
      </div>
    </Dialog>
  );
};

export default DuplicateTabModal;
